import { LoiComparisonFormDto, VendorContractPayoff } from './models/loi-comparison-form.model';
import { TypesOfConsiderationRow } from './models/types-of-consideration-row.model';
import createNotification from '../../../utils/createNotification';
import { getApiErrorDetail } from '../../../utils/getApiErrorDetail';

export async function fetchLoiComparison(
    axios: { get: (url: string) => Promise<{ data: LoiComparisonFormDto[]; status: number }> },
    engagementId: number
): Promise<LoiComparisonFormDto[]> {
    const response = await axios.get(
        `/api/Marketplace/${engagementId}/LoiComparison`
    );
    if (response.status !== 200) {
        throw new Error('Failed to fetch LOI comparison');
    }
    return response.data as LoiComparisonFormDto[];
}

export async function selectAndExecuteLoi(
    axios: { post: (url: string, body: unknown) => Promise<{ status: number }> },
    engagementId: number,
    eventId: string,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiComparison/SelectAndExecute`,
        { eventId, buyerId }
    );
    if (response.status !== 200) {
        throw new Error('Failed to select and execute LOI');
    }
}

export async function setLoiResubmission(
    axios: { post: (url: string, data: unknown) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number,
    isEnabled: boolean
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiComparison/Buyers/${buyerId}/Resubmission/${isEnabled}`,
        null
    );
    if (response.status !== 200) {
        throw new Error('Failed to update resubmission state');
    }
}

export async function startLoiResubmission(
    axios: { post: (url: string, data: unknown) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiResubmission/Buyers/${buyerId}/Start`,
        null
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to start LOI resubmission');
    }
}

export async function completeAdminLoiResubmission(
    axios: { post: (url: string, data: unknown) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiResubmission/Buyers/${buyerId}/AdminComplete`,
        null
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to complete admin LOI resubmission');
    }
}

export async function confirmBuyerLoiResubmission(
    axios: { post: (url: string, data: unknown) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiResubmission/Buyers/${buyerId}/BuyerComplete`,
        null
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to confirm buyer LOI resubmission');
    }
}

export async function approveLoiOnBehalfOfBuyer(
    axios: { post: (url: string, data: null) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiResubmission/Buyers/${buyerId}/ApproveOnBehalfOfBuyer`,
        null
    );
    if (response.status !== 200 && response.status !== 204) {
        const message = getApiErrorDetail(response);
        throw new Error(message ?? 'Failed to approve the LOI form on behalf of the buyer');
    }
}

export async function approveLoiSelection(
    axios: { post: (url: string, body: unknown) => Promise<{ status: number }> },
    engagementId: number,
    eventId: string,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Advisor/${engagementId}/LoiComparison/Approve`,
        { eventId, buyerId }
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to approve LOI selection');
    }
}

export async function declineLoiSelection(
    axios: { post: (url: string, body: unknown) => Promise<{ status: number }> },
    engagementId: number,
    eventId: string,
    buyerId: number,
    comment?: string
): Promise<void> {
    const response = await axios.post(
        `/api/Advisor/${engagementId}/LoiComparison/Decline`,
        { eventId, buyerId, comment }
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to decline LOI selection');
    }
}

export async function saveVendorContractPayoff(
    axios: { patch: (url: string, body: unknown) => Promise<{ data: VendorContractPayoff; status: number }> },
    loiFormId: string,
    body: {
        id?: string;
        tableRowId: number;
        estimatedPayOffAmount?: number | null;
        calculationNotes?: string | null;
    }
): Promise<VendorContractPayoff> {
    const response = await axios.patch(
        `/api/Marketplace/LoiForms/${loiFormId}/VendorContractPayoff`,
        body
    );
    if (response.status !== 200) {
        createNotification('Failed to save liability payoff.', 'error');
        throw new Error('Failed to save vendor contract payoff');
    }
    return response.data as VendorContractPayoff;
}

export async function saveConsiderationRows(
    axios: { put: (url: string, body: unknown) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number,
    rows: TypesOfConsiderationRow[]
): Promise<void> {
    const response = await axios.put(
        `/api/Marketplace/${engagementId}/LoiComparison/${buyerId}/ConsiderationRows`,
        { rows }
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to save consideration rows');
    }
}

export async function saveSfasTotalRetainerFee(
    axios: { put: (url: string, body: unknown) => Promise<{ status: number }> },
    engagementId: number,
    sfasTotalRetainerFee: number,
    reason: string
): Promise<void> {
    const response = await axios.put(
        `/api/Marketplace/Engagements/${engagementId}/SfasTotalRetainerFee`,
        { sfasTotalRetainerFee, reason }
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to save SFA total retainer fee');
    }
}

export async function submitBuyerLoiResubmission(
    axios: { post: (url: string, data: null) => Promise<{ status: number }> },
    engagementId: number,
    buyerId: number
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/LoiResubmission/Buyers/${buyerId}/BuyerSubmit`,
        null
    );
    if (response.status !== 200 && response.status !== 204) {
        const message = getApiErrorDetail(response);
        throw new Error(message ?? 'Failed to submit LOI resubmission');
    }
}
